import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Sun, Moon, Coffee, AlertOctagon, Sparkles, Zap, ChevronRight } from "lucide-react";

function greetingByHour(name) {
  const h = new Date().getHours();
  if (h >= 5 && h < 13)  return { label: "Buenos días",   Icon: Sun,    color: "#E08A1F", emoji: "☀️" };
  if (h >= 13 && h < 20) return { label: "Buenas tardes", Icon: Coffee, color: "#D85A1F", emoji: "🌤" };
  return { label: "Buenas noches", Icon: Moon, color: "#1E5BCC", emoji: "🌙" };
}

export default function GreetingHeader() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [notif, comm] = await Promise.all([
          api.get("/notifications"),
          api.get("/messages/dashboard-summary").catch(() => ({ data: { unread: 0, pendientes_acuse: 0, pendientes_firma: 0 } })),
        ]);
        const mine = (notif.data.items || []).filter((n) => n.responsable?.id && user?.id && n.responsable.id === user.id);
        const cae_mine = mine.filter((n) => n.modulo === "cae").length;
        const fin_mine = mine.filter((n) => ["facturacion", "avales", "garantias"].includes(n.modulo)).length;
        // Encuentra la más urgente: críticas primero, luego más antigua/próxima a vencer
        const sevOrder = { alta: 0, media: 1, baja: 2 };
        const sorted = [...mine].sort((a, b) => (sevOrder[a.severidad] ?? 9) - (sevOrder[b.severidad] ?? 9) || (a.fecha || "").localeCompare(b.fecha || ""));
        const mostUrgent = sorted[0] || null;
        setSummary({
          mine: mine.length,
          mine_critical: mine.filter((n) => n.severidad === "alta").length,
          cae_mine,
          fin_mine,
          team_critical: (notif.data.items || []).filter((n) => n.severidad === "alta" && (!n.responsable?.id || n.responsable.id !== user?.id)).length,
          unread: comm.data.unread || 0,
          firma_pdte: comm.data.pendientes_firma || 0,
          acuse_pdte: comm.data.pendientes_acuse || 0,
          mostUrgent,
        });
      } catch { /* ignore */ }
    };
    load();
  }, [user]);

  const g = greetingByHour(user?.name);
  const firstName = (user?.name || "Operador").split(" ")[0];

  // Construir frase resumen
  const phrases = [];
  if (summary) {
    if (summary.mine_critical > 0) phrases.push(<><b style={{ color: "#DC2626" }}>{summary.mine_critical} {summary.mine_critical === 1 ? "alerta crítica" : "alertas críticas"}</b> para hoy</>);
    else if (summary.mine > 0) phrases.push(<><b className="text-[var(--legatum-text)]">{summary.mine}</b> {summary.mine === 1 ? "alerta asignada" : "alertas asignadas"}</>);
    if (summary.firma_pdte > 0) phrases.push(<><b className="text-[var(--legatum-text)]">{summary.firma_pdte}</b> {summary.firma_pdte === 1 ? "firma pendiente" : "firmas pendientes"}</>);
    if (summary.acuse_pdte > 0 && summary.firma_pdte === 0) phrases.push(<><b className="text-[var(--legatum-text)]">{summary.acuse_pdte}</b> {summary.acuse_pdte === 1 ? "acuse pendiente" : "acuses pendientes"}</>);
    if (summary.unread > 0 && summary.firma_pdte === 0 && summary.acuse_pdte === 0) phrases.push(<><b className="text-[var(--legatum-text)]">{summary.unread}</b> {summary.unread === 1 ? "comunicado sin leer" : "comunicados sin leer"}</>);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-8 md:mb-10"
      data-testid="greeting-header"
    >
      <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--legatum-text-muted)] font-bold inline-flex items-center gap-2">
        <g.Icon className="h-3 w-3" style={{ color: g.color }} />
        Panel de control · {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
      </div>
      <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--legatum-text)]">
        {g.label}, <span style={{ color: "var(--legatum-accent)" }}>{firstName}</span>
      </h1>

      {summary && phrases.length > 0 ? (
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mt-3 text-sm md:text-base text-[var(--legatum-text-secondary)] max-w-3xl flex items-center gap-1.5 flex-wrap"
          data-testid="greeting-summary"
        >
          <Sparkles className="h-3.5 w-3.5 text-[var(--legatum-accent)]" />
          Tienes
          {phrases.map((p, i) => (
            <span key={i} className="font-semibold">{p}{i < phrases.length - 1 ? "," : "."}</span>
          ))}
          {summary.team_critical > 0 && (
            <span className="text-[var(--legatum-text-muted)] text-xs ml-2 inline-flex items-center gap-1">
              <AlertOctagon className="h-3 w-3" /> +{summary.team_critical} críticas del equipo
            </span>
          )}
        </motion.p>
      ) : summary ? (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mt-3 text-sm md:text-base text-[var(--legatum-text-secondary)] max-w-3xl inline-flex items-center gap-1.5"
          data-testid="greeting-summary-empty"
        >
          <Sparkles className="h-3.5 w-3.5 text-[#0F8A5F]" />
          Todo al día — sin alertas asignadas. Buen trabajo. ✨
        </motion.p>
      ) : (
        <p className="mt-3 text-sm md:text-base text-[var(--legatum-text-secondary)] max-w-2xl">
          Toda la operación de LEGATUM CAPITAL — avales, pliegos, obras, garantías — en un único cockpit.
        </p>
      )}

      {/* CTA "Empezar por la más urgente" — sobrio */}
      {summary?.mostUrgent && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-4"
        >
          {(() => {
            const sev = summary.mostUrgent.severidad;
            const accent = sev === "alta" ? "#DC2626" : sev === "media" ? "#E08A1F" : "#1E5BCC";
            return (
              <Link
                to={summary.mostUrgent.ruta || "/"}
                data-testid="greeting-cta-urgent"
                className="inline-flex items-center gap-3 px-4 py-3 rounded-xl transition-colors max-w-2xl group hover:bg-[var(--legatum-elevated)]"
                style={{
                  background: "var(--legatum-surface)",
                  border: "1px solid var(--legatum-border)",
                }}
              >
                <span aria-hidden className="self-stretch w-[3px] rounded-full"
                      style={{ background: accent, opacity: 0.85 }} />
                <div className="relative h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                     style={{ background: `${accent}14`, border: `1px solid ${accent}33` }}>
                  <Zap className="h-5 w-5" style={{ color: accent }} />
                </div>
                <div className="relative min-w-0 flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[var(--legatum-text-muted)]">Empezar por la más urgente</div>
                  <div className="mt-0.5 text-sm md:text-base font-semibold truncate text-[var(--legatum-text)]">{summary.mostUrgent.titulo}</div>
                  <div className="text-xs text-[var(--legatum-text-secondary)] truncate">{summary.mostUrgent.detalle}</div>
                </div>
                <ChevronRight className="relative h-5 w-5 text-[var(--legatum-text-muted)] shrink-0 group-hover:translate-x-1 group-hover:text-[var(--legatum-accent)] transition-all" />
              </Link>
            );
          })()}
        </motion.div>
      )}
    </motion.div>
  );
}
